import { assertTilesetListFits } from "../document/packed-tile.ts";
import type { TileRef, WorldDocument } from "../document/types.ts";
import type { LostEdit } from "../studio/lost-edits.ts";
import { packOf, wholePacksOf } from "./packs.ts";
import type { TerrainNameTable } from "./terrain-names.ts";
/**
 * What the editor knows about the tilesets and prop frames a world can use (ADR-026): display names and
 * groups, each prop's path in its pack and how it plays, and the display names of the packs (the R-0017
 * amendment), independent of the compile context. Plain data, written by the world builder beside the
 * compile context and loaded by the editor.
 */

/**
 * A tileset the palettes can list.
 */
export interface CatalogueTilesetInfo {
  readonly id: string;
  readonly name: string;
  readonly role: "terrain" | "props";
}

/**
 * How a catalogue prop plays: `static` is a still; `loop` and `once` are a strip's playback, a strip that
 * loops wherever it is drawn or a transition strip played only when a thing changes state (ADR-058
 * decision 5).
 */
export type PropPlayback = "static" | "loop" | "once";

/**
 * A prop frame with its display name and group, its path in its pack (forward slashes: its image, or the
 * folder a strip's frames have to themselves) and how it plays. A catalogue written before the R-0017
 * amendment carries neither of the last two. `draft` marks a sprite of a studio pack that is not published
 * (ADR-107 decision 31): any builder may place it, and a world that places it does not publish until its pack does.
 * `words` are the search words the studio gave a sorted pack's frame (ADR-107 E2), absent when it gave none.
 */
export interface CataloguePropInfo {
  readonly tileset: string;
  readonly tile: number;
  readonly name: string;
  readonly group: string;
  readonly words?: readonly string[];
  readonly path?: string;
  readonly plays?: PropPlayback;
  readonly draft?: true;
}

/**
 * A pack a catalogue holds: the prefix of its tileset ids, the display name its host gives it and, when the
 * host has one, the pack's terrain rename table (ADR-038 amendment of 2026-09-26, decision 3).
 */
export interface CataloguePackInfo {
  readonly id: string;
  readonly name: string;
  readonly terrainNames?: TerrainNameTable;
}

/**
 * The editor catalogue of one world. `packs` names the world's packs in its order; a catalogue written
 * before the R-0017 amendment lists none. `lostPulls` names the frames the world pulled that their pack did not
 * hold at the compile that wrote the catalogue (ADR-129 decision 3.12), absent when there was none. `lostEdits` names
 * the studio edits of a sorted pack whose frames the pack no longer has (ADR-107 E8), absent when there was none.
 */
export interface EditorCatalogue {
  readonly tilesets: readonly CatalogueTilesetInfo[];
  readonly props: readonly CataloguePropInfo[];
  readonly packs?: readonly CataloguePackInfo[];
  readonly lostPulls?: readonly TileRef[];
  readonly lostEdits?: readonly LostEdit[];
}

/**
 * A catalogue with nothing in it, for worlds built without one.
 */
export const EMPTY_CATALOGUE: EditorCatalogue = { tilesets: [], props: [] };

/**
 * The display name of a tileset id: the part after the pack prefix, or the id itself.
 *
 * @param tilesetId Tileset id, `<pack>:<name>` or a bare name.
 * @returns The name.
 */
export function tilesetDisplayName(tilesetId: string): string {
  const colon = tilesetId.indexOf(":");
  return colon < 0 ? tilesetId : tilesetId.slice(colon + 1);
}

const NOISE_PREFIX = /^(gl\d+-)?\s*tileset\s*-?\s*(terrain\s*-?\s*)?/i;
const TRANSPARENCY_TAIL = /\s*[-(]?\s*transp(arency)?\)?\s*$/i;
const TRANSPARENCY_INNER = /\s*\(transparency\)\s*/i;

/**
 * A tileset name fit for a heading: the pack's "Tileset -", "Terrain-" and "gl2-" prefixes dropped, a
 * trailing "transparency" read as "(overlay)" since those sets paint over another terrain, and the first
 * letter capitalised.
 *
 * @param tilesetId Tileset id or display name.
 * @returns The heading.
 */
export function tilesetHeading(tilesetId: string): string {
  const raw = tilesetDisplayName(tilesetId);
  let name = raw.replace(NOISE_PREFIX, "").trim();
  const overlay = TRANSPARENCY_TAIL.test(name) || TRANSPARENCY_INNER.test(name);
  name = name.replace(TRANSPARENCY_TAIL, "").replace(TRANSPARENCY_INNER, " ").replace(/\s+/g, " ").trim();
  if (name === "") {
    return raw;
  }
  const capitalised = name.charAt(0).toUpperCase() + name.slice(1);
  return overlay ? `${capitalised} (overlay)` : capitalised;
}

/**
 * The document with every catalogue tileset it lacks of a pack it already lists whole appended, in catalogue
 * order, after the existing entries so tile layers' packed tileset indices stay valid: one whole tileset of a
 * pack becomes the whole pack. A tileset of a pack the document does not list is left out, and a pack the
 * document holds only partial entries of is not widened.
 *
 * It has one caller: the workstation's migration tool (`tools/dev-server/src/migrate-world.ts`), whose widening
 * is a person's own act. No load widens a pack (ADR-129's amendment of 2026-10-13, A4 rule 1): a document opens
 * with the tilesets it lists, and a pack that gains a tileset shows it in the library like any other art, the
 * Packs dialog offering the rest of the pack. A pack is "in the world" only as far as its tilesets are listed.
 *
 * @param world The document.
 * @param catalogue The world's editor catalogue.
 * @returns The widened document, or the same document when nothing is missing.
 * @throws Error when the widened list would hold more entries than the packed tile form indexes.
 */
export function withCatalogueTilesets(world: WorldDocument, catalogue: EditorCatalogue): WorldDocument {
  const known = new Set(world.tilesets.map((t) => t.id));
  const packs = new Set(wholePacksOf(world.tilesets));
  const missing = catalogue.tilesets.filter((t) => !known.has(t.id) && packs.has(packOf(t.id) ?? "")).map((t) => ({ id: t.id }));
  assertTilesetListFits(world.tilesets.length + missing.length, "the tileset list would hold");
  return missing.length === 0 ? world : { ...world, tilesets: [...world.tilesets, ...missing] };
}

/**
 * A pack name of the form `A to B` read as what gets painted (ADR-038): the painted region is A and B
 * surrounds it, so the label says `A on B`. Names without ` to ` are returned unchanged.
 *
 * @param name A wang set or colour name.
 * @returns The label.
 */
export function paintLabel(name: string): string {
  return name.replace(/\bto\b/, "on");
}

/**
 * The entry label of ADR-038 decision 2: the set name alone for a one-colour set, else `<set>: <colour>`, both
 * through `paintLabel`. Terrain layers saved without the `autoName` flag carry it as their name when the editor
 * named them, so it is how such a layer is recognised as following its terrain (`followsTerrain`); displays use
 * `terrainDisplayName`.
 *
 * @param setName Wang set name.
 * @param colourName Colour name, possibly empty.
 * @param colourCount How many colours the set has.
 * @returns The label.
 */
export function terrainEntryLabel(setName: string, colourName: string, colourCount: number): string {
  return colourCount > 1 && colourName !== "" ? `${paintLabel(setName)}: ${paintLabel(colourName)}` : paintLabel(setName);
}

/**
 * The prop frame a catalogue lists under a name, for resolving a game's frame suggestion (ADR-053). Names
 * repeat across sheets, so the caller names the sheet its placed frame came from and that one wins; any
 * other sheet answers only when the preferred one has no such frame.
 *
 * @param catalogue The world's catalogue.
 * @param name The frame name, as the catalogue lists it.
 * @param preferredTileset The tileset to prefer.
 * @returns The frame reference, or undefined when no sheet lists the name.
 */
export function frameNamed(catalogue: EditorCatalogue, name: string, preferredTileset: string): TileRef | undefined {
  const matches = catalogue.props.filter((frame) => frame.name === name);
  const chosen = matches.find((frame) => frame.tileset === preferredTileset) ?? matches[0];
  return chosen === undefined ? undefined : { tileset: chosen.tileset, tile: chosen.tile };
}

/**
 * How a panel names a prop frame: the catalogue's name for it, or its local tile id when no catalogue lists
 * it, so an inspector always has something to show.
 *
 * @param catalogue The world's catalogue.
 * @param frame The frame reference.
 * @returns The display name.
 */
export function frameDisplayName(catalogue: EditorCatalogue, frame: TileRef): string {
  return catalogue.props.find((f) => f.tileset === frame.tileset && f.tile === frame.tile)?.name ?? `tile ${frame.tile}`;
}
